import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@anyseal/api-client";
import type { User } from "@anyseal/contracts";
import { Badge, Button, Field, PageHeader, PageSection, RequestId, Row, StatePanel, Textarea, formatDate, toneForStatus } from "@anyseal/ui";
import { Input as AntInput } from "antd";
import { Fingerprint, KeyRound, Laptop, LockKeyhole, MailCheck, ShieldAlert, ShieldCheck, Smartphone, UserRound } from "lucide-react";
import { portalClient } from "../api";
import { useAuth } from "../auth";
import { FormModal } from "../components/FormModal";
import { useI18n } from "../i18n";
import { statusLabel } from "../status";
import { projectPath, useWorkspace } from "../workspace";

interface Session { id: string; login_method: string; ip_address?: string; user_agent?: string; created_at: string; last_active_at: string; expires_at: string; current: boolean; }
interface Passkey { id: string; name: string; last_used_at?: string; created_at: string; updated_at: string; }
interface TwoFactorStatus { configured: boolean; enabled: boolean; locked_until?: string; recovery_codes_remaining: number; }
interface TwoFactorSetup { secret: string; provisioning_uri: string; recovery_codes: string[]; }
interface OAuthProvider { slug: string; display_name: string; protocol: string; }
interface OAuthStart { authorization_url: string; expires_at: string; }
interface OAuthBinding { id: string; provider_id: string; provider_slug: string; provider_display_name: string; version: number; created_at: string; }
interface DataList<T> { data: T[]; }
interface RiskAction { id: string; action_type: string; status: string; reason: string; appeal_instructions: string; allowed_models: string[]; starts_at: string; expires_at?: string; }
interface RiskAppeal { id: string; action_id: string; statement: string; status: string; resolution?: string; created_at: string; }

export function SecurityPage() {
  const { locale } = useI18n();
  const { logout } = useAuth();
  const { organization, project } = useWorkspace();
  const riskBase = organization && project ? `${projectPath(organization.id, project.id)}/risk` : "";
  const queryClient = useQueryClient();
  const [profileOpen, setProfileOpen] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [twoFactorOpen, setTwoFactorOpen] = useState(false);
  const [twoFactorPassword, setTwoFactorPassword] = useState("");
  const [twoFactorCode, setTwoFactorCode] = useState("");
  const [twoFactorSetup, setTwoFactorSetup] = useState<TwoFactorSetup | null>(null);
  const [passkeyOpen, setPasskeyOpen] = useState(false);
  const [passkeyName, setPasskeyName] = useState("");
  const [passkeyPassword, setPasskeyPassword] = useState("");
  const [passkeyDeleteTarget, setPasskeyDeleteTarget] = useState<Passkey | null>(null);
  const [passkeyDeletePassword, setPasskeyDeletePassword] = useState("");
  const [notice, setNotice] = useState("");
  const [appealAction, setAppealAction] = useState<RiskAction | null>(null);
  const [appealStatement, setAppealStatement] = useState("");
  const me = useQuery({ queryKey: ["portal", "me"], queryFn: () => portalClient.get<User>("/api/portal/v1/me") });
  const sessions = useQuery({ queryKey: ["portal", "sessions"], queryFn: () => portalClient.get<Session[]>("/api/portal/v1/sessions") });
  const passkeys = useQuery({ queryKey: ["portal", "passkeys"], queryFn: () => portalClient.get<Passkey[]>("/api/portal/v1/passkeys") });
  const twoFactor = useQuery({ queryKey: ["portal", "two-factor"], queryFn: () => portalClient.get<TwoFactorStatus>("/api/portal/v1/auth/two-factor") });
  const oauthProviders = useQuery({ queryKey: ["portal", "oauth-providers"], queryFn: () => portalClient.get<OAuthProvider[]>("/api/portal/v1/auth/oauth/providers", { authenticated: false }) });
  const oauthBindings = useQuery({ queryKey: ["portal", "oauth-bindings"], queryFn: () => portalClient.get<OAuthBinding[]>("/api/portal/v1/oauth/bindings") });
  const riskActions = useQuery({ queryKey: ["portal", "risk-actions", riskBase], queryFn: () => portalClient.get<DataList<RiskAction>>(`${riskBase}/actions`), enabled: Boolean(riskBase) });
  const riskAppeals = useQuery({ queryKey: ["portal", "risk-appeals", riskBase], queryFn: () => portalClient.get<DataList<RiskAppeal>>(`${riskBase}/appeals`), enabled: Boolean(riskBase) });
  const profile = useMutation({ mutationFn: () => portalClient.patch<User>("/api/portal/v1/me", { display_name: displayName, locale, expected_version: me.data?.version }), onSuccess: (value) => { queryClient.setQueryData(["portal", "me"], value); setProfileOpen(false); setNotice(locale === "zh-CN" ? "个人资料已更新。" : "Profile updated."); } });
  const password = useMutation({ mutationFn: () => portalClient.put("/api/portal/v1/auth/password", { current_password: currentPassword, new_password: newPassword }), onSuccess: () => void logout() });
  const revokeSession = useMutation({ mutationFn: (id: string) => portalClient.delete(`/api/portal/v1/sessions/${id}`), onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["portal", "sessions"] }) });
  const revokeOthers = useMutation({ mutationFn: () => portalClient.post("/api/portal/v1/sessions/revoke-others"), onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["portal", "sessions"] }) });
  const setupTwoFactor = useMutation({ mutationFn: () => portalClient.post<TwoFactorSetup>("/api/portal/v1/auth/two-factor/setup", { current_password: twoFactorPassword }), onSuccess: setTwoFactorSetup });
  const enableTwoFactor = useMutation({ mutationFn: () => portalClient.post("/api/portal/v1/auth/two-factor/enable", { code: twoFactorCode }), onSuccess: () => void logout() });
  const disableTwoFactor = useMutation({ mutationFn: () => portalClient.delete("/api/portal/v1/auth/two-factor", { code: twoFactorCode }), onSuccess: () => void logout() });
  const createPasskey = useMutation({
    mutationFn: async () => {
      if (!window.PublicKeyCredential) throw new Error(locale === "zh-CN" ? "当前浏览器不支持 Passkey。" : "This browser does not support passkeys.");
      const begin = await portalClient.post<{ options: PublicKeyCredentialCreationOptions | { publicKey: PublicKeyCredentialCreationOptions }; flow_token: string }>("/api/portal/v1/passkeys/registration/options", { name: passkeyName, current_password: passkeyPassword });
      const raw = "publicKey" in begin.options ? begin.options.publicKey : begin.options;
      const publicKey = decodeCreationOptions(raw);
      const credential = await navigator.credentials.create({ publicKey });
      if (!(credential instanceof PublicKeyCredential) || !(credential.response instanceof AuthenticatorAttestationResponse)) throw new Error(locale === "zh-CN" ? "Passkey 创建已取消。" : "Passkey creation was cancelled.");
      return portalClient.post<Passkey>("/api/portal/v1/passkeys/registration/complete", { flow_token: begin.flow_token, credential: serializeAttestation(credential) });
    },
    onSuccess: () => { setPasskeyOpen(false); setPasskeyName(""); setPasskeyPassword(""); void queryClient.invalidateQueries({ queryKey: ["portal", "passkeys"] }); },
  });
  const deletePasskey = useMutation({ mutationFn: ({ id, password: value }: { id: string; password: string }) => portalClient.delete(`/api/portal/v1/passkeys/${id}`, { current_password: value }), onSuccess: () => { setPasskeyDeleteTarget(null); setPasskeyDeletePassword(""); void logout(); } });
  const startOAuthBind = useMutation({ mutationFn: async (provider: OAuthProvider) => portalClient.post<OAuthStart>(`/api/portal/v1/oauth/${encodeURIComponent(provider.slug)}/bind/start`), onSuccess: (start) => location.assign(start.authorization_url) });
  const deleteOAuthBinding = useMutation({ mutationFn: (binding: OAuthBinding) => portalClient.delete(`/api/portal/v1/oauth/bindings/${binding.id}`, { expected_version: binding.version }), onSuccess: () => void logout() });
  const requestEmailVerification = useMutation({ mutationFn: () => portalClient.post("/api/portal/v1/auth/email-verification/request"), onSuccess: () => setNotice(locale === "zh-CN" ? "验证邮件已加入发送队列，请检查邮箱。" : "A verification email has been queued. Check your inbox.") });
  const createRiskAppeal = useMutation({ mutationFn: () => portalClient.post<RiskAppeal>(`${riskBase}/actions/${appealAction?.id}/appeals`, { statement: appealStatement }), onSuccess: () => { setAppealAction(null); setAppealStatement(""); setNotice(locale === "zh-CN" ? "申诉已提交，处理结果会保留在这里。" : "Your appeal was submitted. Its outcome will remain available here."); void riskAppeals.refetch(); } });
  const failure = me.error ?? sessions.error ?? passkeys.error ?? twoFactor.error ?? oauthProviders.error ?? oauthBindings.error ?? riskActions.error ?? riskAppeals.error;
  if (failure) return <StatePanel kind="error" title={locale === "zh-CN" ? "安全设置暂时不可用" : "Security settings unavailable"} description={failure.message} requestId={failure instanceof ApiError ? failure.requestId : undefined} />;
  if (me.isLoading || sessions.isLoading || passkeys.isLoading || twoFactor.isLoading) return <StatePanel kind="loading" title={locale === "zh-CN" ? "正在加载账户安全状态" : "Loading account security"} />;
  const actionError = profile.error ?? password.error ?? revokeSession.error ?? revokeOthers.error ?? setupTwoFactor.error ?? enableTwoFactor.error ?? disableTwoFactor.error ?? createPasskey.error ?? deletePasskey.error ?? startOAuthBind.error ?? deleteOAuthBinding.error ?? requestEmailVerification.error ?? createRiskAppeal.error;
  // Two-factor modal has two submit stages: generate a setup, then confirm with a code.
  const twoFactorEnabled = Boolean(twoFactor.data?.enabled);
  const twoFactorConfirmStage = Boolean(twoFactorSetup) || twoFactorEnabled;

  return (
    <div className="portal-page-grid">
      <PageHeader
        eyebrow="ACCOUNT"
        title={locale === "zh-CN" ? "账户与安全" : "Account & security"}
        description={locale === "zh-CN" ? "管理个人资料、登录凭据、Passkey、双因素认证和活跃会话。" : "Manage your profile, credentials, passkeys, two-factor authentication, and active sessions."}
      />

      {notice ? <div className="portal-notice" role="status"><strong>{notice}</strong></div> : null}
      {actionError ? (
        <div className="portal-form-error" role="alert">
          <strong>{locale === "zh-CN" ? "操作未完成" : "Action not completed"}</strong>
          <span>{actionError.message}</span>
          {actionError instanceof ApiError && actionError.requestId ? <RequestId value={actionError.requestId} /> : null}
        </div>
      ) : null}

      {/* 2-column split: main sections + account status sidebar */}
      <div className="portal-security-split">
        <div className="portal-security-main">
          {/* Profile */}
          <PageSection
            title={locale === "zh-CN" ? "个人资料" : "Profile"}
            description={locale === "zh-CN" ? "显示名称和语言偏好不会改变登录邮箱。" : "Display name and language preference do not change your sign-in email."}
            action={
              <Button size="small" variant="secondary" onClick={() => { setDisplayName(me.data?.display_name ?? ""); setProfileOpen(true); }}>
                {locale === "zh-CN" ? "编辑" : "Edit"}
              </Button>
            }
          >
            <Row
              icon={<UserRound size={20} />}
              title={me.data?.display_name}
              detail={me.data?.email}
              action={<Badge tone={toneForStatus(me.data?.status ?? "")}>{statusLabel(me.data?.status, locale)}</Badge>}
            />
          </PageSection>

          {/* Sign-in protection */}
          <PageSection
            title={locale === "zh-CN" ? "登录保护" : "Sign-in protection"}
            description={locale === "zh-CN" ? "安全方式发生变化后，现有会话会失效，需要重新登录。" : "Changing a security method revokes existing sessions and requires a new sign-in."}
          >
            <Row
              icon={<LockKeyhole size={20} />}
              title={locale === "zh-CN" ? "密码" : "Password"}
              detail={locale === "zh-CN" ? "使用至少 12 位的唯一密码" : "Use a unique password with at least 12 characters"}
              action={<Button size="small" variant="secondary" onClick={() => setPasswordOpen(true)}>{locale === "zh-CN" ? "更改" : "Change"}</Button>}
            />
            <Row
              icon={<Fingerprint size={20} />}
              title="Passkey"
              detail={`${passkeys.data?.length ?? 0} ${locale === "zh-CN" ? "个已注册" : "registered"}`}
              action={<Button size="small" variant="secondary" onClick={() => setPasskeyOpen(true)}>{locale === "zh-CN" ? "添加" : "Add"}</Button>}
            />
            <Row
              icon={<ShieldCheck size={20} />}
              title={locale === "zh-CN" ? "双因素认证" : "Two-factor authentication"}
              detail={twoFactor.data?.enabled ? `${locale === "zh-CN" ? "已启用，剩余恢复码" : "Enabled, recovery codes"}: ${twoFactor.data.recovery_codes_remaining}` : (locale === "zh-CN" ? "尚未启用" : "Not enabled")}
              action={<Button size="small" variant="secondary" onClick={() => setTwoFactorOpen(true)}>{twoFactor.data?.enabled ? (locale === "zh-CN" ? "管理" : "Manage") : (locale === "zh-CN" ? "设置" : "Set up")}</Button>}
            />
            <Row
              icon={<MailCheck size={20} />}
              title={locale === "zh-CN" ? "邮箱验证" : "Email verification"}
              detail={me.data?.email_verified_at ? formatDate(me.data.email_verified_at, locale) : (locale === "zh-CN" ? "Relay 使用前必须完成验证" : "Verification is required before Relay use")}
              action={me.data?.email_verified_at ? <Badge tone="success">{locale === "zh-CN" ? "已验证" : "Verified"}</Badge> : <Button size="small" variant="secondary" busy={requestEmailVerification.isPending} onClick={() => requestEmailVerification.mutate()}>{locale === "zh-CN" ? "发送验证邮件" : "Send verification email"}</Button>}
            />
            {(passkeys.data ?? []).length === 0
              ? <StatePanel kind="empty" title={locale === "zh-CN" ? "还没有 Passkey" : "No passkeys yet"} />
              : passkeys.data?.map((item) => (
                  <Row
                    key={item.id}
                    icon={<KeyRound size={20} />}
                    title={item.name}
                    detail={`${locale === "zh-CN" ? "创建" : "Created"} ${formatDate(item.created_at, locale)} · ${locale === "zh-CN" ? "最后使用" : "Last used"} ${formatDate(item.last_used_at, locale)}`}
                    action={
                      <Button size="small" variant="quiet" onClick={() => {
                        setPasskeyDeleteTarget(item);
                        setPasskeyDeletePassword("");
                      }}>
                        {locale === "zh-CN" ? "删除" : "Delete"}
                      </Button>
                    }
                  />
                ))
            }
          </PageSection>

        </div>

        {/* Account status sidebar */}
        <div className="portal-security-sidebar">
          <AccountStatusCard
            locale={locale}
            me={me.data}
            twoFactor={twoFactor.data}
            passkeys={passkeys.data}
            sessions={sessions.data}
          />
        </div>
      </div>
      <PageSection
        title={locale === "zh-CN" ? "请求限制与申诉" : "Request restrictions & appeals"}
        description={locale === "zh-CN" ? "生效中的处置会在请求发往供应商之前执行；每一项都可以提交申诉。" : "Active actions are enforced before a request reaches a provider, and every action can be appealed."}
      >
        {(riskActions.data?.data ?? []).length === 0
          ? riskActions.isLoading
            ? <StatePanel kind="loading" title={locale === "zh-CN" ? "正在加载处置状态" : "Loading restrictions"} />
            : <StatePanel kind="empty" title={locale === "zh-CN" ? "当前项目没有生效中的处置" : "No active actions for this project"} />
          : riskActions.data?.data.map((item) => {
              const pending = riskAppeals.data?.data.some((appeal) => appeal.action_id === item.id && appeal.status === "pending");
              return (
                <Row
                  key={item.id}
                  icon={<ShieldAlert size={20} />}
                  title={<><Badge tone="warning">{statusLabel(item.action_type, locale)}</Badge> <code>{item.id}</code></>}
                  detail={<>{item.reason}<br />{item.appeal_instructions}<br />{locale === "zh-CN" ? "有效期" : "Window"}: {formatDate(item.starts_at, locale)} → {item.expires_at ? formatDate(item.expires_at, locale) : "∞"}{item.allowed_models.length ? <><br />{locale === "zh-CN" ? "允许模型" : "Allowed models"}: {item.allowed_models.join(", ")}</> : null}</>}
                  action={<Button size="small" variant="secondary" disabled={pending} onClick={() => setAppealAction(item)}>{pending ? (locale === "zh-CN" ? "等待审理" : "Pending") : (locale === "zh-CN" ? "申诉" : "Appeal")}</Button>}
                />
              );
            })
        }
        {appealAction ? (
          <form className="portal-auth-form" style={{ marginTop: 4 }} onSubmit={(event: FormEvent) => { event.preventDefault(); createRiskAppeal.mutate(); }}>
            <Field label={locale === "zh-CN" ? `针对 ${appealAction.id} 的申诉陈述` : `Appeal statement for ${appealAction.id}`} required>
              <Textarea value={appealStatement} onChange={(event) => setAppealStatement(event.target.value)} maxLength={4000} required />
            </Field>
            <div className="portal-inline-actions">
              <Button type="submit" busy={createRiskAppeal.isPending}>{locale === "zh-CN" ? "提交申诉" : "Submit appeal"}</Button>
              <Button type="button" variant="quiet" onClick={() => { setAppealAction(null); setAppealStatement(""); }}>{locale === "zh-CN" ? "取消" : "Cancel"}</Button>
            </div>
          </form>
        ) : null}
        {(riskAppeals.data?.data ?? []).length > 0
          ? riskAppeals.data?.data.map((item) => (
              <Row
                key={item.id}
                icon={<ShieldCheck size={20} />}
                title={`${locale === "zh-CN" ? "申诉" : "Appeal"} · ${item.action_id}`}
                detail={<>{item.statement}{item.resolution ? <><br />{locale === "zh-CN" ? "处理结果" : "Resolution"}: {item.resolution}</> : null}<br />{formatDate(item.created_at, locale)}</>}
                action={<Badge tone={toneForStatus(item.status)}>{statusLabel(item.status, locale)}</Badge>}
              />
            ))
          : null}
      </PageSection>

      {/* External identities */}
      <PageSection
        title={locale === "zh-CN" ? "外部身份" : "External identities"}
        description={locale === "zh-CN" ? "绑定 OAuth 或 OIDC 身份后可从对应身份提供商登录。解绑会撤销当前会话。" : "Linked OAuth or OIDC identities can be used to sign in. Unlinking revokes the current session."}
      >
        {oauthProviders.data?.filter((provider) => !oauthBindings.data?.some((binding) => binding.provider_slug === provider.slug)).map((provider) => (
          <div key={provider.slug} className="portal-inline-actions" style={{ marginBottom: 4 }}>
            <Button size="small" variant="secondary" busy={startOAuthBind.isPending} onClick={() => startOAuthBind.mutate(provider)}>
              {locale === "zh-CN" ? `绑定 ${provider.display_name}` : `Link ${provider.display_name}`}
            </Button>
          </div>
        ))}
        {(oauthBindings.data ?? []).length === 0
          ? <StatePanel kind="empty" title={locale === "zh-CN" ? "尚未绑定外部身份" : "No external identities linked"} />
          : oauthBindings.data?.map((binding) => (
              <Row
                key={binding.id}
                icon={<ShieldCheck size={20} />}
                title={binding.provider_display_name}
                detail={`${binding.provider_slug} · ${locale === "zh-CN" ? "绑定于" : "Linked"} ${formatDate(binding.created_at, locale)}`}
                action={<Button size="small" variant="quiet" busy={deleteOAuthBinding.isPending} onClick={() => deleteOAuthBinding.mutate(binding)}>{locale === "zh-CN" ? "解绑" : "Unlink"}</Button>}
              />
            ))
        }
      </PageSection>

      {/* Active sessions */}
      <PageSection
        title={locale === "zh-CN" ? "活跃会话" : "Active sessions"}
        description={locale === "zh-CN" ? "如果发现不认识的设备，请立即撤销并更改密码。" : "If you do not recognize a device, revoke it immediately and change your password."}
        action={<Button size="small" variant="secondary" busy={revokeOthers.isPending} onClick={() => revokeOthers.mutate()}>{locale === "zh-CN" ? "退出其他设备" : "Sign out other devices"}</Button>}
      >
        {sessions.data?.map((item) => (
          <Row
            key={item.id}
            icon={/mobile|iphone|android/i.test(item.user_agent ?? "") ? <Smartphone size={20} /> : <Laptop size={20} />}
            title={item.user_agent || (locale === "zh-CN" ? "未知设备" : "Unknown device")}
            detail={`${item.ip_address || "—"} · ${locale === "zh-CN" ? "最近活动" : "Last active"} ${formatDate(item.last_active_at, locale)}`}
            action={item.current ? <Badge tone="brand">{locale === "zh-CN" ? "当前会话" : "Current"}</Badge> : <Button size="small" variant="quiet" disabled={revokeSession.isPending} onClick={() => revokeSession.mutate(item.id)}>{locale === "zh-CN" ? "撤销" : "Revoke"}</Button>}
          />
        ))}
      </PageSection>

      {/* Profile edit */}
      <FormModal
        open={profileOpen}
        title={locale === "zh-CN" ? "编辑个人资料" : "Edit profile"}
        onCancel={() => setProfileOpen(false)}
        onSubmit={() => profile.mutate()}
        submitLabel={locale === "zh-CN" ? "保存" : "Save"}
        busy={profile.isPending}
        error={profile.error}
      >
        <label>
          <span>{locale === "zh-CN" ? "显示名称" : "Display name"}</span>
          <AntInput value={displayName} onChange={(event) => setDisplayName(event.target.value)} required />
        </label>
      </FormModal>

      {/* Password change */}
      <FormModal
        open={passwordOpen}
        title={locale === "zh-CN" ? "更改密码" : "Change password"}
        description={locale === "zh-CN" ? "更改密码后，所有会话将失效并需要重新登录。" : "Changing your password revokes all sessions and requires a new sign-in."}
        onCancel={() => setPasswordOpen(false)}
        onSubmit={() => password.mutate()}
        submitLabel={locale === "zh-CN" ? "更改并重新登录" : "Change and sign in again"}
        busy={password.isPending}
        error={password.error}
      >
        <label>
          <span>{locale === "zh-CN" ? "当前密码" : "Current password"}</span>
          <AntInput type="password" autoComplete="current-password" value={currentPassword} onChange={(event) => setCurrentPassword(event.target.value)} required />
        </label>
        <label>
          <span>{locale === "zh-CN" ? "新密码" : "New password"}</span>
          <AntInput type="password" minLength={12} autoComplete="new-password" value={newPassword} onChange={(event) => setNewPassword(event.target.value)} required />
        </label>
      </FormModal>

      {/* Passkey add */}
      <FormModal
        open={passkeyOpen}
        title={locale === "zh-CN" ? "添加 Passkey" : "Add passkey"}
        onCancel={() => setPasskeyOpen(false)}
        onSubmit={() => createPasskey.mutate()}
        submitLabel={locale === "zh-CN" ? "创建 Passkey" : "Create passkey"}
        busy={createPasskey.isPending}
        error={createPasskey.error}
      >
        <label>
          <span>{locale === "zh-CN" ? "Passkey 名称" : "Passkey name"}</span>
          <AntInput value={passkeyName} onChange={(event) => setPasskeyName(event.target.value)} placeholder={locale === "zh-CN" ? "例如：办公电脑" : "For example: Work laptop"} required />
        </label>
        <label>
          <span>{locale === "zh-CN" ? "当前密码（身份确认）" : "Current password (verification)"}</span>
          <AntInput type="password" autoComplete="current-password" value={passkeyPassword} onChange={(event) => setPasskeyPassword(event.target.value)} required />
        </label>
      </FormModal>

      {/* Passkey delete — password confirmed in a modal, never a browser prompt */}
      <FormModal
        open={Boolean(passkeyDeleteTarget)}
        title={locale === "zh-CN" ? "删除 Passkey" : "Delete passkey"}
        description={locale === "zh-CN"
          ? `删除 “${passkeyDeleteTarget?.name ?? ""}” 后需要重新登录。此操作不可撤销。`
          : `Deleting “${passkeyDeleteTarget?.name ?? ""}” revokes your session. This cannot be undone.`}
        onCancel={() => { setPasskeyDeleteTarget(null); setPasskeyDeletePassword(""); }}
        onSubmit={() => passkeyDeleteTarget && deletePasskey.mutate({ id: passkeyDeleteTarget.id, password: passkeyDeletePassword })}
        submitLabel={locale === "zh-CN" ? "删除并重新登录" : "Delete and sign in again"}
        busy={deletePasskey.isPending}
        disabled={!passkeyDeletePassword}
        danger
        error={deletePasskey.error}
      >
        <label>
          <span>{locale === "zh-CN" ? "当前密码（身份确认）" : "Current password (verification)"}</span>
          <AntInput type="password" autoComplete="current-password" value={passkeyDeletePassword} onChange={(event) => setPasskeyDeletePassword(event.target.value)} required />
        </label>
      </FormModal>

      {/* Two-factor setup / manage */}
      <FormModal
        open={twoFactorOpen}
        title={twoFactorEnabled ? (locale === "zh-CN" ? "管理双因素认证" : "Manage two-factor authentication") : (locale === "zh-CN" ? "设置双因素认证" : "Set up two-factor authentication")}
        onCancel={() => setTwoFactorOpen(false)}
        onSubmit={() => {
          if (!twoFactorConfirmStage) setupTwoFactor.mutate();
          else if (twoFactorEnabled) disableTwoFactor.mutate();
          else enableTwoFactor.mutate();
        }}
        submitLabel={
          !twoFactorConfirmStage
            ? (locale === "zh-CN" ? "生成验证器配置" : "Generate authenticator setup")
            : twoFactorEnabled
            ? (locale === "zh-CN" ? "停用并重新登录" : "Disable and sign in again")
            : (locale === "zh-CN" ? "启用并重新登录" : "Enable and sign in again")
        }
        danger={twoFactorEnabled}
        busy={setupTwoFactor.isPending || enableTwoFactor.isPending || disableTwoFactor.isPending}
        error={setupTwoFactor.error ?? enableTwoFactor.error ?? disableTwoFactor.error}
      >
        {!twoFactorConfirmStage ? (
          <label>
            <span>{locale === "zh-CN" ? "当前密码（身份确认）" : "Current password (verification)"}</span>
            <AntInput type="password" value={twoFactorPassword} onChange={(event) => setTwoFactorPassword(event.target.value)} required />
          </label>
        ) : null}
        {twoFactorSetup ? (
          <div className="portal-secret">
            <strong>{locale === "zh-CN" ? "立即保存恢复码" : "Save recovery codes now"}</strong>
            <p>{locale === "zh-CN" ? "把以下密钥添加到验证器，并离线保存恢复码。" : "Add this secret to your authenticator and store recovery codes offline."}</p>
            <code>{twoFactorSetup.secret}</code>
            <code>{twoFactorSetup.recovery_codes.join("  ")}</code>
          </div>
        ) : null}
        {twoFactorConfirmStage ? (
          <label>
            <span>{locale === "zh-CN" ? "验证器代码或恢复码" : "Authenticator or recovery code"}</span>
            <AntInput autoComplete="one-time-code" value={twoFactorCode} onChange={(event) => setTwoFactorCode(event.target.value)} required />
          </label>
        ) : null}
      </FormModal>
    </div>
  );
}

function AccountStatusCard({ locale, me, twoFactor, passkeys, sessions }: {
  locale: string;
  me: User | undefined;
  twoFactor: { configured: boolean; enabled: boolean } | undefined;
  passkeys: { id: string }[] | undefined;
  sessions: { id: string }[] | undefined;
}) {
  const isChinese = locale === "zh-CN";
  const rows = [
    {
      label: isChinese ? "邮箱验证" : "Email",
      value: me?.email_verified_at ? (isChinese ? "已验证" : "Verified") : (isChinese ? "未验证" : "Unverified"),
      dot: me?.email_verified_at ? "ok" : "warn",
    },
    {
      label: isChinese ? "双因素认证" : "2FA",
      value: twoFactor?.enabled ? (isChinese ? "已启用" : "Enabled") : (isChinese ? "未启用" : "Disabled"),
      dot: twoFactor?.enabled ? "ok" : "warn",
    },
    {
      label: "Passkeys",
      value: `${passkeys?.length ?? 0}`,
      dot: (passkeys?.length ?? 0) > 0 ? "ok" : "off",
    },
    {
      label: isChinese ? "活跃会话" : "Sessions",
      value: `${sessions?.length ?? 0}`,
      dot: "ok" as const,
    },
  ];
  return (
    <div className="portal-security-status">
      <div className="portal-security-status__head">
        <span className="portal-security-status__title">{isChinese ? "账户状态" : "Account status"}</span>
      </div>
      <div className="portal-security-status__body">
        {rows.map((row) => (
          <div key={row.label} className="portal-security-status__row">
            <span>{row.label}</span>
            <span>
              <span className={`portal-security-status__dot portal-security-status__dot--${row.dot}`} />
              {row.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function decodeCreationOptions(value: PublicKeyCredentialCreationOptions): PublicKeyCredentialCreationOptions {
  return { ...value, challenge: decodeBase64URL(value.challenge as unknown as string), user: { ...value.user, id: decodeBase64URL(value.user.id as unknown as string) }, excludeCredentials: value.excludeCredentials?.map((item) => ({ ...item, id: decodeBase64URL(item.id as unknown as string) })) };
}
function decodeBase64URL(value: string): ArrayBuffer { const base64 = value.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(value.length / 4) * 4, "="); const bytes = Uint8Array.from(atob(base64), (char) => char.charCodeAt(0)); return bytes.buffer; }
function encodeBase64URL(value: ArrayBuffer): string { const bytes = new Uint8Array(value); let text = ""; bytes.forEach((byte) => { text += String.fromCharCode(byte); }); return btoa(text).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, ""); }
function serializeAttestation(credential: PublicKeyCredential) { const response = credential.response as AuthenticatorAttestationResponse; return { id: credential.id, rawId: encodeBase64URL(credential.rawId), type: credential.type, response: { attestationObject: encodeBase64URL(response.attestationObject), clientDataJSON: encodeBase64URL(response.clientDataJSON), transports: response.getTransports?.() ?? [] }, clientExtensionResults: credential.getClientExtensionResults() }; }
